import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";

const Resume = () => {
  return (
    <Layout>
      <SEO
        title="Work"
        description="From engineer to startup founder. A non-linear career path through technology, products, and people."
        type="website"
        url="/resume"
        keywords={["Bekah June work history", "software engineer", "GitHub", "CircleCI", "startup founder"]}
      />
      
      {/* Hero Section - Full Page Image */}
      <section className="relative min-h-screen flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: 'url(/theroadtotheriver.jpg)' }}>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Content */}
        <div className="relative z-10 w-full px-6 md:px-12 text-center">
          <h1 className="text-5xl md:text-8xl font-bold leading-none tracking-tight text-white drop-shadow-2xl">
            Résumé
          </h1>
        </div>

        {/* Attribution */}
        <div className="absolute bottom-8 right-8 text-right">
          <p className="text-white/60 text-xs drop-shadow">
            <span className="italic">The Road to the River</span>, 2025<br />
            Suprunchuk Yuriy
          </p>
        </div>
      </section>

      {/* New Dark Section */}
      <section style={{ backgroundColor: '#070717' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <h3 className="font-['Source_Serif_4'] text-[18px] font-bold uppercase tracking-wide" style={{ color: '#FFFDFC' }}>
            A DECADE
          </h3>
          <div className="text-left space-y-8" style={{ color: '#FFFDFC' }}>
            <p className="text-base leading-relaxed">
              I've been working full-time as an adult for a decade. Ten years.
            </p>
            <p className="text-base leading-relaxed">
              I got my first "real" job at 20. Crazy to think about now, but that's the perk of dropping 
              out of college. That first job was rough; I personally cannot recommend hiring a 20-year-old. 
              And yet, it was the first job that set up my career.
            </p>
            <p className="text-base leading-relaxed">
              Most resumes are short with clear, actionable bullet points on what you did at your job. 
              They usually state the title, the company, and the time you spent there. But after a decade 
              of working, I've realized that a traditional resume doesn't quite capture the context, 
              reflections, or learnings from each job. So, I've decided to do something different.
            </p>
            <p className="text-base leading-relaxed">
              This is a narrative resume and is a truer representation of my career, its twists and turns, highlights 
              and lowlights, all in one place. My career has felt less like a ladder and more like a winding 
              trail, with some stops along the way.
            </p>
            <p className="text-base leading-relaxed">
              So if you're still with me, let's go on an adventure and look back at what my career has 
              actually been like, how it's shaped my thinking, my curiosity, and me personally.
            </p>
            <p className="text-base leading-relaxed">
              Unlike most resumes, which start with the most recent job and work backward, I'm going to 
              place you where any good story does... the beginning.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: Engineer - Blue */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#EBF2FF' }}>
        {/* Background watermark text - top left */}
        <div className="absolute top-0 left-2 sm:left-4 md:left-8 pointer-events-none select-none -translate-y-[30%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(31, 10, 151, 0.15)' }}>
            Software
          </h2>
        </div>
        
        {/* Background watermark text - bottom right */}
        <div className="absolute bottom-0 right-2 sm:right-4 md:right-8 pointer-events-none select-none translate-y-[15%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(31, 10, 151, 0.15)' }}>
            Engineer
          </h2>
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="space-y-12">
            {/* Various Startups */}
            <div className="space-y-3">
              <h3 className="font-['Source_Serif_4'] text-[18px] font-bold uppercase tracking-wide" style={{ color: '#1F0A97' }}>
                THE BEGINNING
              </h3>
              <div className="prose prose-resume-engineer max-w-none">
                <p>
                  I genuinely can't recall the title of my first job, but I was hired to manage WordPress 
                  sites for a health company. It was early 2016, and I was young, hopeful, and scared. I 
                  remember feeling intimidated by the few co-workers and the boss.
                </p>
                <p>
                  What I do remember was reading and learning about PHP, HTML, and CSS. This was part of 
                  keeping the sites looking polished, updating the text, and ensuring we could repurpose 
                  them. I would go home, open my laptop, and watch videos to learn more about PHP, explore 
                  images in CSS and HTML, and try to understand how it was all connected.
                </p>
                <p>
                  This first job unlocked a curiosity in me that would lead me to every job after.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#1F0A97' }}>
                  — moved to austin, texas —
                </p>
                <p>
                  After learning to code at two coding bootcamps, I landed my first job as a{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>Software Engineer at Cratejoy in 2017</span>. 
                  This job came about because an incredible design lead thought I would do a good job 
                  implementing the new design system for their B2B product, and he was right.
                </p>
                <p>
                  Cratejoy lasted for a year, but like many startups, it went through layoffs, and I was 
                  not spared.
                </p>
                <p>
                  Thankfully, I landed another job at{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>SignEasy as a Support Engineer</span>,{" "}
                  where I worked alongside some fellow Ukrainians to solve technical issues for our 
                  customers. This job gave me the opportunity to rub shoulders with some very smart 
                  engineers at an incredibly bootstrapped company, teaching me about hard work, India, 
                  and scale.
                </p>
                <p>
                  But I was restless. It wasn't quite the right fit, so I started looking for something 
                  that paid better and tapped into my creative side. That's how I landed a new job at{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>1Password as a Software Engineer in 2019</span>.
                </p>
                <p>
                  This was my first contract and fully remote job, working with a global team. My time 
                  here was short, and I'll tell you why: I was fired. Yes, I was fired. Why? A couple of 
                  reasons.
                </p>
                <p>
                  First, they wanted me, as a junior engineer, to completely rewrite their JS codebase to 
                  TypeScript. Second, they lacked a clear CI/CD system for catching security issues. So, 
                  although my contract was terminated without a clear reason, I believe it was because I 
                  was in over my head and made mistakes that mattered in a security-focused company.
                </p>
                <p>
                  That job taught me that you must also interview the company, manager, and role. You, as 
                  an applicant, have a duty of due diligence to ensure you're the best fit for the role, 
                  just as much as they do. It also taught me the importance of test suites and CI security 
                  scans.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#1F0A97' }}>
                  — break to find employment —
                </p>
                <p>
                  After picking myself up from that disaster, I needed something to help rebuild my 
                  confidence.
                </p>
                <p>
                  A friend mentioned that a design firm he worked at needed some engineering help, and I 
                  jumped at the opportunity. After securing the job as a{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>Software Engineer at Handsome</span>,{" "}
                  I remember having a quiet moment in which I vowed to work smarter than I ever had 
                  before, to remove all distractions from my workday, and to never get fired again. I 
                  had just finished Cal Newport's <em>Deep Work</em> and <em>So Good They Can't Ignore 
                  You</em>, which reshaped how I thought about my work and approached my job.
                </p>
                <p>
                  My time at Handsome was short but transformational. This was the job that turned my 
                  career around. I believe wholeheartedly it was because I was working with designers. The 
                  design team took their work seriously and treated it with the respect it deserved as 
                  craftsmanship. My code was craft. My work was craft. With deliberate focus, incredible 
                  mentorship, and creative energy, I built things I am still proud of.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#1F0A97' }}>
                  — a moment for reflection —
                </p>
                <p className="mt-12">
                  I look back on these jobs with so much empathy, heartache, and amusement. Empathy for 
                  the young woman navigating the workforce before most even enter it. Heartache for all 
                  the mistakes, missed opportunities, and misjudgments I made. But mainly I feel 
                  amusement. Joyful for the learnings, laughing at my own stupidity, and quietly grateful 
                  for all the incredible mentors who took a risk on me: Stu, Tim, Jennifer, Sean, and more.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Architect - Yellow */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#FFD700' }}>
        {/* Background watermark text - top left */}
        <div className="absolute top-0 left-2 sm:left-4 md:left-8 pointer-events-none select-none -translate-y-[30%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(184, 134, 11, 0.2)' }}>
            Solutions
          </h2>
        </div>
        
        {/* Background watermark text - bottom right */}
        <div className="absolute bottom-0 right-2 sm:right-4 md:right-8 pointer-events-none select-none translate-y-[15%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(184, 134, 11, 0.2)' }}>
            Architect
          </h2>
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="space-y-12">
            {/* Solutions Architect Journey */}
            <div className="space-y-3">
              <h3 className="font-['Source_Serif_4'] text-[18px] font-bold uppercase tracking-wide" style={{ color: '#B8860B' }}>
                GROWING UP
              </h3>
              <div className="prose prose-resume-architect max-w-none">
                <p>
                  After learning the importance of continuous integration in development, I took a shot at applying to CircleCI, the number one company in this area.
                </p>
                <p>
                  I landed my first{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>Solutions Architect role at CircleCI in late 2019</span>, but didn't start until January 2020.
                </p>
                <p>
                  This role was unlike anything I'd done before. It was technical. It was customer-facing. It was commission-based and highly specialized in Developer Operations.
                </p>
                <p>
                  A whole new world.
                </p>
                <p>
                  This job taught me a lot about developers. What developers need, how they operate, what they dislike, and more. You learn the most about a group of people when they are your direct customers.
                </p>
                <p>
                  Learning to demo refined my understanding of storytelling in a very specific way. When you demo a product, you can quickly gauge how interested or uninterested a customer is, but that doesn't correlate well with whether the customer will buy. You learn to adapt to your audience, tell a story for a specific user, and identify their pain points, hoping your product will alleviate them.
                </p>
                <p>
                  On reflection, this role suited my storytelling abilities and my curiosity about customers. I love finding the in-betweens and the unsaids with customers; it's like sleuthing.
                </p>
                <p>
                  There were also some very real challenges in my time at CircleCI.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#B8860B' }}>
                  — a trigger warning —
                </p>
                <p>
                  I experienced my first brush with sexual harassment in the workplace.
                </p>
                <p>
                  I had never navigated this before, and I assumed, as an out lesbian, this wouldn't be something I had to worry about. Yet it happened, and it was terrible. CircleCI handled it better than most stories I've heard, and yet in hindsight, I think that experience shifted how I worked and operated afterwards.
                </p>
                <p>
                  Safety is everything, and the moment it's lost, it can be extremely difficult to recreate what once felt safe.
                </p>
                <p>
                  My calls with predominantly male customers felt more bristled and put me on edge, questioning if they didn't purchase because I was a woman. It made me question promotion decisions in ways I had been blissfully ignorant of.
                </p>
                <p>
                  I learned that politics goes far in corporate culture, further than craft. I learned that people will disregard you simply because of their biases. I learned that work is just that, work, and that no matter how hard you work or how deserving you believe you are, you can still get overlooked. And I say this truthfully: it's not personal. It's an operational system that wasn't built for the era we saw in 2020.
                </p>
                <p>
                  It also taught me about competition. I was part of a team that conducted competitive analysis, and we learned a lot about how our competitors operated, sold, priced, and more. We presented our findings to the Board, and I continued to highlight GitHub Actions as our biggest threat.
                </p>
                <p>
                  I was so adamant that this tiny product, which was a mix of a Zapier product and an attempt at a CI solution at the time, would eat our lunch. So much so that when I left for my next job, my manager joked, "Are you going to work for the Actions team?"
                </p>
                <p>
                  I wasn't.
                </p>
                <p>
                  I went back to Handsome.
                </p>
                <p>
                  Tired of the politics and needing that creative zest I'd found previously, I rejoined{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>Handsome as a Solutions Architect in late 2021</span>.
                </p>
                <p>
                  My time there was short, but I worked with some incredible developers, designers, and business people.
                </p>
                <p>
                  On reflection, I think I was in a bit of a haze and may have been experiencing some slight burnout from CircleCI. We don't really get the time we need to rest between our jobs, no matter how intense or jarring they can be, and I brought some of that mental baggage with me.
                </p>
                <p>
                  The team was gracious enough, and I pushed through until I got a DM on LinkedIn one day that was about to turn my career upside down.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#B8860B' }}>
                  — a moment for reflection —
                </p>
                <p>
                  I look back at these couple of years and see them as the catalyst for the pattern recognition that is essential to product thinking. The ability to quickly spot a competitor before anyone else, whether it's gut or more, you pick up the subtleties at play.
                </p>
                <p>
                  I also see the shaping of a leader. Sometimes it takes hard times in our jobs to really have us emerge with clarity on who we are and what we're capable of. This was that time. I'd been working as a leader without the title or recognition, but the muscle was there, and I was ready to use it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Product - Black */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#070717' }}>
        {/* Background watermark text - top left */}
        <div className="absolute top-0 left-2 sm:left-4 md:left-8 pointer-events-none select-none -translate-y-[30%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none text-white/20">
            Director of
          </h2>
        </div>
        
        {/* Background watermark text - bottom right */}
        <div className="absolute bottom-0 right-2 sm:right-4 md:right-8 pointer-events-none select-none translate-y-[15%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none text-white/20">
            Product
          </h2>
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="space-y-12">
            {/* GitHub */}
            <div className="space-y-3">
              <h3 className="font-['Source_Serif_4'] text-[18px] font-bold uppercase tracking-wide text-white">
                LEADERSHIP
              </h3>
              <div className="prose prose-resume-product max-w-none">
                <p>
                  There are certain jobs and opportunities that fundamentally change everything. This was that.
                </p>
                <p>
                  Becoming a{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>Director of Product at GitHub in 2022</span>{" "}
                  was a dream and an opportunity. It was the Handsome moment mirrored back to me, when I vowed to work smarter, focus, and be so good they couldn't ignore me.
                </p>
                <p>
                  I spent almost three years at GitHub, and I genuinely can't say a bad thing about my time there.
                </p>
                <p>
                  I can say I disagreed with people, decisions, and other corporate matters, but I don't have anything bad to say about GitHub.
                </p>
                <p>
                  My time there was mainly spent under the revenue organization as an influential Director of Product for GitHub Actions and compute products. The job was a bit different and not quite as tethered as a traditional product job.
                </p>
                <p>
                  The foundation of the job was customers.
                </p>
                <p>
                  Everything about my job was focused on the customer. What did we need to build in order to serve the customer? How could we improve what already existed to serve the customer? Why did the customer buy or not buy our products?
                </p>
                <p>
                  GitHub's work culture was very high-performance, and I didn't realize that until I left. You begin to adapt and mesh with the flow of a culture without realizing exactly what it is. GitHub worked smart, hard, and sometimes frantically.
                </p>
                <p>
                  I joined GitHub right as the AI boom hit, and I got to watch as Copilot soared. The product changed everything. It changed the company's focus, it changed the company's revenue, and it changed the company's competitive landscape.
                </p>
                <p>
                  Working with the Actions team was incredible. The PMs, engineering team, and Actions Specialist teams were wonderful, smart, and constantly evangelizing.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12 text-white/80">
                  — like any job, nothing is perfect —
                </p>
                <p>
                  Due to changing focus and priorities, I started to feel increasingly apathetic about the area I was working in. I wanted to switch roles and move around the company. For one thing, working under an organization that is not Product but attempting to influence the roadmap and product decisions on behalf of the customer was extremely challenging.
                </p>
                <p>
                  Within a couple of years, I started pushing towards partnerships and working with the partnership team. At a company like GitHub, internal jobs and movement can take a long time. It took a while and a lot of advocacy, but eventually I was able to join the Strategic AI Partnerships team within the Product organization.
                </p>
                <p>
                  Although my time on that team was very short, I got to see how AI was about to shift everything within GitHub. I advocated heavily for an even more mature partnership ecosystem, as I believed, and still do, that the future of AI requires context and tools.
                </p>
                <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12 text-white/80">
                  — a moment for reflection —
                </p>
                <p>
                  GitHub taught me how to think deliberately about decisions. It taught me how to answer very hard questions. It taught me the importance and power of transparency. It taught me that transparency leads to accountability and accountability leads to outcomes. It taught me that some of the smartest people are also the kindest, and you can create a culture of high performance without losing that.
                </p>
                <p>
                  My time at GitHub was truly transformational. It provided me with the opportunities to grow, the capital to set out on my own, and the confidence to chase my dreams.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Founder - Light Red/Pink */}
      <section className="relative overflow-hidden bg-[#FFE8EB]">
        {/* Background watermark text - top left */}
        <div className="absolute top-0 left-2 sm:left-4 md:left-8 pointer-events-none select-none -translate-y-[30%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(199, 17, 34, 0.12)' }}>
            CEO &
          </h2>
        </div>
        
        {/* Background watermark text - bottom right */}
        <div className="absolute bottom-0 right-2 sm:right-4 md:right-8 pointer-events-none select-none translate-y-[15%]">
          <h2 className="text-[56px] sm:text-[64px] md:text-[90px] lg:text-[120px] xl:text-[140px] font-bold tracking-wide uppercase leading-none" style={{ color: 'rgba(199, 17, 34, 0.12)' }}>
            Founder
          </h2>
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="space-y-12">
            {/* Chalant */}
            <div className="space-y-3">
            <h3 className="font-['Source_Serif_4'] text-[18px] font-bold uppercase tracking-wide" style={{ color: '#c71122' }}>
                MY DREAM
              </h3>
              <div className="prose prose-resume-founder max-w-none">
                <p>
                  I set out to build my first company in early 2025. It was the first time I had embarked 
                  on an entrepreneurial quest, and I was excited to be the{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>CEO & Co-Founder of Chalant</span>.
                </p>
                <p>
                  Chalant was a social media app focused on superfans - you could join a fandom, chat with 
                  other users, and connect over what you loved. It was a continuation of an idea I'd had 
                  back in 2021 called Stanly.
                </p>
                <p>
                  I built the entire web and iOS application in 5 months, and we gained over 1,000 users.
                </p>
                <p>
                  <Link 
                    to="/blog/my-first-company"
                    className="border-b-2 hover:opacity-70 transition-opacity"
                    style={{ borderColor: '#FF4952', color: 'inherit', textDecoration: 'none' }}
                  >
                    Chalant closed its doors
                  </Link>
                  , but I learned more in those 8 months than I had in a long time. 
                  I learned about legal entities, co-founder relationships, and what users seek in consumer 
                  applications. I also learned a lot about myself, what I wanted to build, and how I would 
                  build my next company differently.
                </p>
              </div>
            </div>

            {/* House of June */}
            <div className="space-y-3">
              <div className="prose prose-resume-founder max-w-none">
              <p className="font-['Nothing_You_Could_Do',_cursive] text-center font-bold !text-[22px] my-12" style={{ color: '#c71122' }}>
                  — the present —
                </p>
                <p>
                  We've finally arrived at the present. I founded my second company,{" "}
                  <span className="font-['Source_Serif_4'] text-[16px] font-bold uppercase tracking-wide" style={{ color: '#FF4952' }}>House of June, in January of 2026</span>.
                </p>
                <p>
                  House of June is on a mission to create media, products, and technology for artists and 
                  audiences with a focus on craft, experience, and heritage.
                </p>
                <p>
                  Our first product, Enjoy, is a space for people to review, save, and connect over their 
                  favorite media.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Resume;
